import create from 'zustand';
import MediaService from '../../services/MediaService';
import { useProfile } from './useProfile';

const VOLUME_KEY = 'song-share:volume';
const MUTE_KEY = 'song-share:muted';
const LOOP_KEY = 'song-share:loop';
const AUTOPLAY_KEY = 'song-share:autoplay';

const readNumber = (key: string, fallback: number): number => {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    const parsed = Number(raw);
    if (Number.isNaN(parsed)) return fallback;
    return Math.min(1, Math.max(0, parsed));
  } catch {
    return fallback;
  }
};

const readBoolean = (key: string, fallback: boolean): boolean => {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return raw === 'true';
  } catch {
    return fallback;
  }
};

// null = the user hasn't chosen; the effective default is decided elsewhere
// (auth-aware for autoplay).
const readNullableBoolean = (key: string): boolean | null => {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    return raw === 'true';
  } catch {
    return null;
  }
};

const writeString = (key: string, value: string) => {
  try { localStorage.setItem(key, value); } catch { /* ignore */ }
};

export type PlayerState = {
  isPaused: boolean,
  isOver: boolean,
  volume: number,
  isMuted: boolean,
  isVolumeOpen: boolean,
  loop: boolean,
  // null = no explicit choice yet; autoplayEnabled() resolves the effective
  // value (defaults on for logged-out users, off for logged-in).
  autoplay: boolean | null,
  playNow: () => void,
  pauseNow: () => void,
  songIsOver: () => void,
  setVolume: (value: number) => void,
  toggleMute: () => void,
  setVolumeOpen: (open: boolean) => void,
  toggleLoop: () => void,
  toggleAutoplay: () => void,
  autoplayEnabled: () => boolean,
}


export const usePlayer = create<PlayerState>((set, get) => ({
  isPaused: true,
  isOver: true,
  volume: readNumber(VOLUME_KEY, 1),
  isMuted: readBoolean(MUTE_KEY, false),
  isVolumeOpen: false,
  loop: readBoolean(LOOP_KEY, false),
  autoplay: readNullableBoolean(AUTOPLAY_KEY),
  playNow: () => {
    set({ isPaused: false, isOver: false });
  },
  pauseNow: () => {
    set({ isPaused: true })
  },
  songIsOver: () => {
    set({ isOver: true })
  },
  setVolume: (value) => {
    const clamped = Math.min(1, Math.max(0, value));
    set({ volume: clamped, isMuted: false });
    writeString(VOLUME_KEY, String(clamped));
    writeString(MUTE_KEY, 'false');
    MediaService.please().setVolume(clamped);
  },
  toggleMute: () => {
    const next = !get().isMuted;
    set({ isMuted: next });
    writeString(MUTE_KEY, String(next));
    MediaService.please().setVolume(next ? 0 : get().volume);
  },
  setVolumeOpen: (open) => set({ isVolumeOpen: open }),
  toggleLoop: () => {
    const next = !get().loop;
    set({ loop: next });
    writeString(LOOP_KEY, String(next));
  },
  // Effective autoplay: an explicit choice wins; otherwise default on for
  // logged-out visitors (continuous listening without an account) and off for
  // logged-in users.
  autoplayEnabled: () => {
    const pref = get().autoplay;
    if (pref !== null) return pref;
    return !useProfile.getState().authenticated;
  },
  toggleAutoplay: () => {
    const next = !get().autoplayEnabled();
    set({ autoplay: next });
    writeString(AUTOPLAY_KEY, String(next));
  },
}));
